import { Card, Empty, Pagination, Spin } from 'antd';
import type { ReactNode } from 'react';

interface MobileCardListPagination {
  current: number;
  pageSize: number;
  total: number;
  onChange: (page: number) => void;
}

/**
 * Randarea pe mobil a unei liste care pe desktop e un `Table`: fiecare rând
 * devine un `Card`. Paginarea e complet controlată (server-side) printr-un
 * `Pagination` separat — NU folosim slice-ul client-side al listelor AntD,
 * fiindcă `dataSource` conține deja doar pagina curentă venită de la server
 * (aceleași semantici ca la Table-ul de pe desktop).
 */
export function MobileCardList<T>({
  dataSource,
  rowKey,
  loading,
  renderCard,
  onCardClick,
  emptyText = 'Nicio înregistrare.',
  pagination,
}: {
  dataSource: T[] | undefined;
  rowKey: (row: T) => string;
  loading?: boolean;
  renderCard: (row: T) => ReactNode;
  onCardClick?: (row: T) => void;
  emptyText?: ReactNode;
  pagination?: MobileCardListPagination;
}) {
  const rows = dataSource ?? [];
  return (
    <Spin spinning={!!loading}>
      {rows.length === 0 && !loading ? (
        <Empty description={emptyText} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {rows.map((row) => (
            <Card
              key={rowKey(row)}
              size="small"
              onClick={onCardClick ? () => onCardClick(row) : undefined}
              style={onCardClick ? { cursor: 'pointer' } : undefined}
              styles={{ body: { padding: 12 } }}
            >
              {renderCard(row)}
            </Card>
          ))}
        </div>
      )}
      {pagination && pagination.total > pagination.pageSize ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={pagination.onChange}
            showSizeChanger={false}
            simple
          />
        </div>
      ) : null}
    </Spin>
  );
}

/**
 * Rând etichetă–valoare într-un card de listă mobilă: eticheta gri la stânga,
 * valoarea aliniată la dreapta. Reutilizat de cardurile din Facturi/Cheltuieli.
 */
export function CardRow({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', marginTop: 4 }}>
      <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 13, flex: '0 0 auto' }}>{label}</span>
      <span style={{ textAlign: 'right', minWidth: 0 }}>{children}</span>
    </div>
  );
}
